import * as Popover from '@radix-ui/react-popover';
import { HelpCircle } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { cn } from '../../lib/cn';

export type FieldHelpIconProps = {
  text: string;
  className?: string;
  dataTestId?: string;
};

const HOVER_CLOSE_DELAY_MS = 140;

/** Подсказка по элементу: иконка «?», текст открывается наведением, кликом/тапом и фокусом. */
export function FieldHelpIcon({ text, className, dataTestId }: FieldHelpIconProps) {
  const [open, setOpen] = useState(false);
  const closeTimerRef = useRef<number | null>(null);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current != null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const openNow = useCallback(() => {
    clearCloseTimer();
    setOpen(true);
  }, [clearCloseTimer]);

  const scheduleClose = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => setOpen(false), HOVER_CLOSE_DELAY_MS);
  }, [clearCloseTimer]);

  return (
    <Popover.Root
      open={open}
      onOpenChange={(next) => {
        clearCloseTimer();
        setOpen(next);
      }}
    >
      <Popover.Trigger asChild>
        <button
          type="button"
          data-testid={dataTestId}
          className={cn(
            'inline-flex size-6 shrink-0 items-center justify-center rounded-sm text-[hsl(var(--ui-muted-foreground))] outline-none hover:text-[hsl(var(--ui-foreground))] focus-visible:ring-2 focus-visible:ring-[hsl(var(--ui-ring))]',
            className
          )}
          aria-label={`Справка: ${text}`}
          aria-expanded={open}
          onPointerEnter={openNow}
          onPointerLeave={scheduleClose}
          onFocus={openNow}
          onBlur={scheduleClose}
        >
          <HelpCircle className="size-3.5" aria-hidden />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          role="tooltip"
          side="top"
          align="start"
          sideOffset={6}
          collisionPadding={8}
          onOpenAutoFocus={(event) => event.preventDefault()}
          onPointerEnter={clearCloseTimer}
          onPointerLeave={scheduleClose}
          className="z-[5602] max-w-xs rounded-md border border-[hsl(var(--ui-border))] bg-[hsl(var(--ui-card))] px-3 py-2 text-xs leading-snug text-[hsl(var(--ui-foreground))] shadow-md outline-none"
        >
          {text}
          <Popover.Arrow className="fill-[hsl(var(--ui-card))]" width={10} height={5} />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
